import './chart-tooltip.css';
import { createContext, useContext, useId, useState, type ReactNode } from 'react';

export interface ChartTooltipDatum {
  title: string;
  label: string;
  value: string;
  color?: string;
  context?: string;
}

interface ActiveTooltip {
  datum: ChartTooltipDatum;
  anchorId?: string;
}

interface ChartTooltipContextValue {
  setActive: (tooltip: ActiveTooltip | null) => void;
  activeAnchorId?: string;
}

const ChartTooltipContext = createContext<ChartTooltipContextValue | null>(null);

export function ChartTooltipGroup({ children, className = '' }: { children: ReactNode; className?: string }) {
  const [active, setActive] = useState<ActiveTooltip | null>(null);

  const contextValue: ChartTooltipContextValue = active?.anchorId ? { setActive, activeAnchorId: active.anchorId } : { setActive };
  return <ChartTooltipContext.Provider value={contextValue}>
    <div className={`chart-tooltip-group${className ? ` ${className}` : ''}`}>
      {children}
      <div className={`chart-tooltip-slot${active ? ' is-active' : ''}${active?.anchorId ? ' is-pointer' : ''}`} aria-hidden={active ? undefined : 'true'}>
        {active && <ChartTooltipPopup datum={active.datum} />}
      </div>
    </div>
  </ChartTooltipContext.Provider>;
}

export function ChartTooltipPopup({ datum, className = '' }: { datum: ChartTooltipDatum; className?: string }) {
  return <span className={`chart-tooltip-popup${className ? ` ${className}` : ''}`} aria-hidden="true">
    <strong>{datum.title}</strong>
    <span><svg className="chart-tooltip-swatch" viewBox="0 0 12 12" aria-hidden="true"><rect x="1" y="1" width="10" height="10" fill={datum.color ?? '#6e9fe0'} /></svg>{datum.label}: {datum.value}</span>
    {datum.context && <small>{datum.context}</small>}
  </span>;
}

export function ChartTooltipMark({ datum, className = '', children, hoverOnly = false }: { datum: ChartTooltipDatum; className?: string; children: ReactNode; hoverOnly?: boolean }) {
  const context = useContext(ChartTooltipContext);
  const anchorId = useId();
  const accessibleLabel = [datum.title, datum.label, datum.value, datum.context].filter(Boolean).join(' · ');
  return <div
    className={`chart-tooltip-mark${className ? ` ${className}` : ''}`}
    data-tooltip-active={context?.activeAnchorId === anchorId ? 'true' : undefined}
    role="img"
    tabIndex={0}
    aria-label={accessibleLabel}
    onPointerEnter={() => context?.setActive(hoverOnly ? { datum, anchorId } : { datum })}
    onPointerLeave={() => context?.setActive(null)}
    onFocus={() => context?.setActive(hoverOnly ? { datum, anchorId } : { datum })}
    onBlur={() => context?.setActive(null)}
  >
    {children}
  </div>;
}

export function ChartTooltipSvgMark({ datum, className = '', children, hoverOnly = false }: { datum: ChartTooltipDatum; className?: string; children: ReactNode; hoverOnly?: boolean }) {
  const context = useContext(ChartTooltipContext);
  const anchorId = useId();
  const accessibleLabel = [datum.title, datum.label, datum.value, datum.context].filter(Boolean).join(' · ');
  return <g
    className={`chart-tooltip-svg-mark${className ? ` ${className}` : ''}`}
    data-tooltip-active={context?.activeAnchorId === anchorId ? 'true' : undefined}
    role="img"
    tabIndex={0}
    aria-label={accessibleLabel}
    onPointerEnter={() => context?.setActive(hoverOnly ? { datum, anchorId } : { datum })}
    onPointerLeave={() => context?.setActive(null)}
    onFocus={() => context?.setActive(hoverOnly ? { datum, anchorId } : { datum })}
    onBlur={() => context?.setActive(null)}
  >
    {children}
  </g>;
}

export function ChartBarMark({ datum, className = '', shapeClassName = '', valuePercent }: { datum: ChartTooltipDatum; className?: string; shapeClassName?: string; valuePercent: number }) {
  const height = Math.max(0, Math.min(100, Number.isFinite(valuePercent) ? valuePercent : 0));
  return <svg className={`chart-bar-mark ${className}`.trim()} viewBox="0 0 100 100" preserveAspectRatio="none" role="group" aria-label={`${datum.title} · ${datum.label}: ${datum.value}`}>
    <ChartTooltipSvgMark datum={datum} hoverOnly>
      <rect className={`chart-bar-mark-shape ${shapeClassName}`.trim()} x="8" y={100 - height} width="84" height={height} rx="4" />
    </ChartTooltipSvgMark>
  </svg>;
}
